import { useState, useEffect, useRef, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { ChevronLeft, ChevronRight } from "lucide-react";
import SEO from "@/components/SEO";

const PHOTOS = [
  {
    src: "/model-1.webp",
    label: "Editorial · I",
    alt:
      "Alex Soto (ALEKZ) editorial photography — dark portrait with warm light streak, New York model.",
    caption:
      "Alex Soto editorial portrait — dark cinematic lighting, black t-shirt.",
  },
  {
    src: "/model-2.webp",
    label: "Editorial · II",
    alt:
      "ALEKZ (Alex Soto) motion blur editorial portrait — arms crossed, New York-based model.",
    caption:
      "Alekz Soto motion editorial — long-exposure blur, silver chain, black tee.",
  },
  {
    src: "/model-3.webp",
    label: "Editorial · III",
    alt:
      "Alex Soto model portrait — deep red backdrop, silver chains, black t-shirt studio editorial.",
    caption:
      "Alex Soto studio editorial — red backdrop, silver chains, direct look.",
  },
  {
    src: "/model-4.webp",
    label: "Editorial · IV",
    alt:
      "Alex Soto (Alekz Soto) editorial — red backdrop, tattooed forearms, watch and chain.",
    caption:
      "ALEKZ editorial — red backdrop, tattoo detail, warm smile portrait.",
  },
  {
    src: "/model-5.webp",
    label: "Editorial · V",
    alt:
      "Alexander Soto (ALEKZ) profile editorial — red backdrop, sleeve tattoo, silver chain.",
    caption:
      "Alexander Soto profile editorial — sleeve tattoo detail, side portrait.",
  },
  {
    src: "/model-6.webp",
    label: "Editorial · VI",
    alt:
      "Alex Soto (ALEKZ) full-length editorial portrait — arms crossed, tattoo sleeve, wide denim, black t-shirt on white studio backdrop.",
    caption:
      "Alex Soto studio editorial — full-length on stool, arms crossed, wide denim.",
  },
  {
    src: "/model-7.webp",
    label: "Editorial · VII",
    alt:
      "Alex Soto (Alekz Soto) editorial portrait — side gaze, silver chain, black t-shirt, wide denim, minimal white studio.",
    caption:
      "Alekz Soto editorial — seated side gaze, minimal white studio, wide denim.",
  },
];

const BASE_URL = "https://alekzsoto.com";

export default function Modeling() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const total = PHOTOS.length;
  const touchStartX = useRef(null);

  const go = useCallback(
    (dir) => setIndex((i) => (i + dir + total) % total),
    [total]
  );
  const goTo = (i) => setIndex(((i % total) + total) % total);

  // Auto-rotate every ~6.5s (paused on hover / when lightbox is open)
  useEffect(() => {
    if (paused || lightbox) return;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % total);
    }, 6500);
    return () => clearInterval(id);
  }, [paused, lightbox, total, index]);

  // Keyboard arrows
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(false);
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go]);

  // Lock body scroll while lightbox is open
  useEffect(() => {
    if (!lightbox) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [lightbox]);

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
    touchStartX.current = null;
  };

  const prevIdx = (index - 1 + total) % total;
  const nextIdx = (index + 1) % total;

  return (
    <section
      className="relative min-h-screen pt-32 sm:pt-40 px-6 sm:px-10 lg:px-14 pb-24"
      data-testid="modeling-page"
    >
      <SEO
        path="/modeling"
        title="Modeling"
        description={`Alex Soto (ALEKZ, Alekz Soto, Alexander Soto) modeling portfolio — editorial and studio photography. New York-based model, DJ, and creative. 5'11", based in NYC.`}
      />

      {/* Structured data — ImageGallery with each editorial as an ImageObject */}
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ImageGallery",
          "name": "ALEKZ — Alex Soto Modeling",
          "description":
            "Editorial modeling portfolio of Alex Soto (ALEKZ, Alekz Soto, Alexander Soto). New York-based model.",
          "url": `${BASE_URL}/modeling`,
          "author": {
            "@type": "Person",
            "name": "Alex Soto",
            "alternateName": ["ALEKZ", "Alekz Soto", "Alexander Soto"],
            "url": BASE_URL,
            "jobTitle": ["Model", "DJ", "Techno Producer", "Creative"],
            "height": {"@type": "QuantitativeValue", "value": "5'11\"", "unitText": "feet inches"},
            "address": {"@type": "PostalAddress", "addressLocality": "New York", "addressRegion": "NY", "addressCountry": "US"}
          },
          "image": PHOTOS.map((p, i) => ({
            "@type": "ImageObject",
            "contentUrl": `${BASE_URL}${p.src}`,
            "url": `${BASE_URL}${p.src}`,
            "name": `Alex Soto — ${p.label}`,
            "description": p.caption,
            "caption": p.caption,
            "creditText": "Alex Soto (ALEKZ)",
            "creator": {"@type": "Person", "name": "Alex Soto"},
            "position": i + 1
          }))
        })}</script>
      </Helmet>

      {/* Visually hidden semantic content — helps crawlers surface every name variant + "model" */}
      <div className="sr-only">
        <h1>Alex Soto — ALEKZ | Modeling Portfolio</h1>
        <p>
          Editorial modeling portfolio of Alex Soto, also known as ALEKZ, Alekz
          Soto, and Alexander Soto. Alex Soto is a New York-based model, DJ and
          techno producer. Height 5'11". This gallery features editorial and
          studio photography of Alex Soto (ALEKZ model, alex soto model, alekz
          soto model, alexander soto model).
        </p>
        <ul>
          {PHOTOS.map((p) => (
            <li key={p.src}>{p.caption}</li>
          ))}
        </ul>
      </div>

      <div className="max-w-6xl mx-auto">
        <h2
          className="font-display text-white leading-[0.95]"
          style={{ fontSize: "clamp(3rem, 9vw, 7rem)", letterSpacing: "-0.01em" }}
          data-testid="modeling-title"
        >
          Modeling
        </h2>

        {/* Stats */}
        <div
          className="mt-6 flex items-center gap-6 font-mono-wide text-[10px] tracking-[0.4em] text-[var(--silver-soft)]"
          data-testid="modeling-stats"
        >
          <span>
            <span className="text-[var(--silver)]/60 mr-2">Height</span>
            <span className="text-white">5&apos;11&quot;</span>
          </span>
          <span className="h-3 w-px bg-[var(--hairline-strong)]" />
          <span>
            <span className="text-[var(--silver)]/60 mr-2">Based</span>
            <span className="text-white">New York</span>
          </span>
        </div>

        <div className="mt-10 h-px bg-[var(--hairline-strong)] line-grow" />

        {/* Carousel */}
        <div
          className="mt-12 sm:mt-16 relative"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          data-testid="modeling-carousel"
        >
          {/* Stage — the three-frame cinematic setup: prev · main · next */}
          <div className="relative flex items-center justify-center min-h-[62vh] md:min-h-[68vh] select-none">
            {/* Prev peek (desktop only) */}
            <button
              type="button"
              onClick={() => go(-1)}
              aria-label="Previous photo"
              data-testid="carousel-prev-peek"
              className="hidden md:block absolute left-0 lg:left-4 top-1/2 -translate-y-1/2 w-[16vw] max-w-[220px] aspect-[3/4] opacity-35 hover:opacity-70 transition-opacity duration-500"
              style={{
                filter: "grayscale(50%) brightness(0.65)",
                maskImage:
                  "linear-gradient(to right, rgba(0,0,0,0) 0%, rgba(0,0,0,0.85) 60%, rgba(0,0,0,0.4) 100%)",
                WebkitMaskImage:
                  "linear-gradient(to right, rgba(0,0,0,0) 0%, rgba(0,0,0,0.85) 60%, rgba(0,0,0,0.4) 100%)",
              }}
            >
              <img
                src={PHOTOS[prevIdx].src}
                alt=""
                aria-hidden
                className="w-full h-full object-cover"
                draggable={false}
              />
            </button>

            {/* Main frame */}
            <button
              type="button"
              onClick={() => setLightbox(true)}
              aria-label="Expand photo"
              className="group relative w-[86vw] sm:w-[62vw] md:w-[46vw] lg:w-[38vw] xl:w-[34vw] max-w-[560px] aspect-[3/4] cursor-zoom-in"
              data-testid="carousel-active-frame"
            >
              {/* Silver hairline frame corners */}
              <span aria-hidden className="absolute -top-3 -left-3 w-6 h-px bg-[var(--silver)]/60" />
              <span aria-hidden className="absolute -top-3 -left-3 w-px h-6 bg-[var(--silver)]/60" />
              <span aria-hidden className="absolute -top-3 -right-3 w-6 h-px bg-[var(--silver)]/60" />
              <span aria-hidden className="absolute -top-3 -right-3 w-px h-6 bg-[var(--silver)]/60" />
              <span aria-hidden className="absolute -bottom-3 -left-3 w-6 h-px bg-[var(--silver)]/60" />
              <span aria-hidden className="absolute -bottom-3 -left-3 w-px h-6 bg-[var(--silver)]/60" />
              <span aria-hidden className="absolute -bottom-3 -right-3 w-6 h-px bg-[var(--silver)]/60" />
              <span aria-hidden className="absolute -bottom-3 -right-3 w-px h-6 bg-[var(--silver)]/60" />

              {/* Photo stack — cross-fade */}
              <div className="absolute inset-0 overflow-hidden bg-black">
                {PHOTOS.map((p, i) => (
                  <img
                    key={p.src}
                    src={p.src}
                    alt={p.alt}
                    draggable={false}
                    data-testid={`carousel-photo-${i}`}
                    className={`absolute inset-0 w-full h-full object-cover transition-all duration-[900ms] ease-[cubic-bezier(0.2,0.8,0.2,1)] ${
                      i === index
                        ? "opacity-100 scale-100"
                        : "opacity-0 scale-105"
                    }`}
                    loading={i === index ? "eager" : "lazy"}
                  />
                ))}
                {/* Subtle vignette + film grain feel */}
                <div
                  aria-hidden
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(ellipse at center, rgba(0,0,0,0) 55%, rgba(0,0,0,0.45) 95%, rgba(0,0,0,0.7) 100%)",
                  }}
                />
              </div>

              {/* Index caption */}
              <div className="absolute -bottom-10 left-0 right-0 flex items-center justify-between">
                <span
                  className="font-mono-wide text-[10px] tracking-[0.4em] text-[var(--silver-soft)]"
                  data-testid="carousel-label"
                >
                  {PHOTOS[index].label}
                </span>
                <span
                  className="font-mono-wide text-[10px] tracking-[0.4em] text-[var(--silver-soft)]"
                  data-testid="carousel-counter"
                >
                  {String(index + 1).padStart(2, "0")}
                  <span className="text-[var(--silver)]/30 mx-2">/</span>
                  {String(total).padStart(2, "0")}
                </span>
              </div>
            </button>

            {/* Next peek (desktop only) */}
            <button
              type="button"
              onClick={() => go(1)}
              aria-label="Next photo"
              data-testid="carousel-next-peek"
              className="hidden md:block absolute right-0 lg:right-4 top-1/2 -translate-y-1/2 w-[16vw] max-w-[220px] aspect-[3/4] opacity-35 hover:opacity-70 transition-opacity duration-500"
              style={{
                filter: "grayscale(50%) brightness(0.65)",
                maskImage:
                  "linear-gradient(to left, rgba(0,0,0,0) 0%, rgba(0,0,0,0.85) 60%, rgba(0,0,0,0.4) 100%)",
                WebkitMaskImage:
                  "linear-gradient(to left, rgba(0,0,0,0) 0%, rgba(0,0,0,0.85) 60%, rgba(0,0,0,0.4) 100%)",
              }}
            >
              <img
                src={PHOTOS[nextIdx].src}
                alt=""
                aria-hidden
                className="w-full h-full object-cover"
                draggable={false}
              />
            </button>
          </div>

          {/* Controls */}
          <div className="mt-20 sm:mt-24 flex items-center justify-center gap-8">
            <button
              type="button"
              onClick={() => go(-1)}
              aria-label="Previous"
              data-testid="carousel-prev"
              className="silver-chip w-10 h-10 rounded-full inline-flex items-center justify-center"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            {/* Ticker dots */}
            <div className="flex items-center gap-3" role="tablist">
              {PHOTOS.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => goTo(i)}
                  aria-label={`Go to photo ${i + 1}`}
                  data-testid={`carousel-dot-${i}`}
                  className="group py-2"
                >
                  <span
                    className={`block h-px transition-all duration-500 ${
                      i === index
                        ? "w-10 bg-[var(--silver)]"
                        : "w-5 bg-[var(--silver)]/30 group-hover:bg-[var(--silver)]/60"
                    }`}
                  />
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={() => go(1)}
              aria-label="Next"
              data-testid="carousel-next"
              className="silver-chip w-10 h-10 rounded-full inline-flex items-center justify-center"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Lightbox — expanded photo view */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md flex items-center justify-center px-4 sm:px-10 py-16"
          onClick={() => setLightbox(false)}
          data-testid="modeling-lightbox"
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setLightbox(false);
            }}
            aria-label="Close"
            data-testid="lightbox-close"
            className="absolute top-6 right-6 sm:top-10 sm:right-10 font-mono-wide text-[10px] tracking-[0.4em] text-[var(--silver)] hover:text-white transition-colors"
          >
            Close ✕
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              go(-1);
            }}
            aria-label="Previous"
            data-testid="lightbox-prev"
            className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 silver-chip w-11 h-11 rounded-full inline-flex items-center justify-center"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          <div
            className="relative max-w-[92vw] max-h-[86vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={PHOTOS[index].src}
              alt={PHOTOS[index].alt}
              className="max-w-full max-h-[86vh] w-auto h-auto object-contain block fade-in"
              data-testid="lightbox-image"
              draggable={false}
            />
            <span className="absolute -bottom-8 left-0 font-mono-wide text-[10px] tracking-[0.4em] text-[var(--silver-soft)]">
              {PHOTOS[index].label}
            </span>
            <span className="absolute -bottom-8 right-0 font-mono-wide text-[10px] tracking-[0.4em] text-[var(--silver-soft)]">
              {String(index + 1).padStart(2, "0")}
              <span className="text-[var(--silver)]/30 mx-2">/</span>
              {String(total).padStart(2, "0")}
            </span>
          </div>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              go(1);
            }}
            aria-label="Next"
            data-testid="lightbox-next"
            className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 silver-chip w-11 h-11 rounded-full inline-flex items-center justify-center"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </section>
  );
}
